<template>
  <div class="h-full">
    <!-- Mobile sidebar -->
    <div 
      v-show="isOpen" 
      class="fixed inset-0 z-50 lg:hidden"
      @click="$emit('close')"
    >
      <div class="fixed inset-0 bg-background/80 backdrop-blur-sm transition-opacity"></div>
    </div>

    <!-- Sidebar content -->
    <div 
      class="fixed inset-y-0 left-0 z-50 w-64 bg-surface/45 dark:bg-[#050B18]/75 backdrop-blur-xl border-r border-border shadow-[0_0_50px_rgba(0,0,0,0.3)] transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 lg:relative lg:flex-shrink-0 h-full flex flex-col theme-transition"
      :class="isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'"
    >
      <!-- Sidebar header -->
      <div class="flex items-center justify-between h-16 px-6 border-b border-border">
        <div class="flex items-center">
          <!-- Light mode logo -->
          <img 
            src="@/assets/main.png" 
            alt="Parking System Logo" 
            class="w-full h-full object-contain block dark:hidden"
          />
          <!-- Dark mode logo -->
          <img 
            src="@/assets/main-light.png" 
            alt="Parking System Logo" 
            class="w-full h-full object-contain hidden dark:block"
          />
        </div>
        
        <!-- Close button for mobile -->
        <button 
          @click="$emit('close')"
          class="lg:hidden p-2 rounded-xl text-text-secondary hover:text-text-primary hover:bg-surface-hover focus:outline-none focus:ring-2 focus:ring-[#A6FF00] theme-transition"
          aria-label="Close sidebar"
        >
          <XMarkIcon class="w-5 h-5" />
        </button>
      </div>

      <!-- Navigation menu -->
      <nav class="flex-1 px-4 py-6 space-y-2 overflow-y-auto min-h-0 scrollbar-custom flex flex-col">
        <div v-for="section in navigationSections" :key="section.name" class="space-y-1">
          <!-- Section header -->
          <h3 class="px-3 text-[10px] font-black text-text-muted uppercase tracking-widest mb-2 mt-4">
            {{ section.name }} 
          </h3>
          
          <!-- Section items -->
          <div class="space-y-1">
            <router-link
              v-for="item in section.items"
              :key="item.name"
              :to="item.path"
              :class="[
                'nav-item group flex items-center px-3 py-2.5 text-sm font-bold rounded-xl transition-all duration-200',
                isActive(item.path)
                  ? 'nav-item-active text-text-primary'
                  : 'text-text-secondary hover:text-[#A6FF00] nav-item-hover'
              ]"
              @click="$emit('navigate')"
            >
              <component 
                :is="item.icon" 
                :class="[
                  'mr-3 h-5 w-5 transition-colors duration-200',
                  isActive(item.path)
                    ? 'text-[#A6FF00]'
                    : 'text-text-muted group-hover:text-[#A6FF00]'
                ]"
              />
              {{ item.name }}
              
              <!-- Badge for notifications -->
              <span 
                v-if="item.badge"
                class="ml-auto inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold"
                :class="item.badge.type === 'error' ? 'bg-danger/10 text-danger' : 'bg-[#A6FF00]/10 text-[#A6FF00]'"
              >
                {{ item.badge.count }}
              </span>
            </router-link>
          </div>
        </div>

        <!-- Spacer to push status widget and footer to bottom -->
        <div class="flex-1"></div>

        <!-- Parking Status Widget (Sidebar centerpiece) -->
        <div class="mt-6 px-3 py-3.5 bg-background/50 border border-border rounded-2xl shadow-sm">
          <p class="text-[9px] font-black uppercase tracking-widest text-text-muted mb-2 flex items-center gap-1.5">
            <span class="w-1.5 h-1.5 rounded-full bg-[#A6FF00] animate-pulse"></span>
            Parking Status
          </p>
          <div class="flex items-center justify-between mb-1">
            <span class="text-[11px] text-text-secondary font-bold">Occupancy</span>
            <span class="text-[11px] text-[#A6FF00] font-black">{{ occupancyPct }}%</span>
          </div>
          <div class="w-full bg-background h-1.5 rounded-full overflow-hidden">
            <div 
              class="h-full bg-gradient-to-r from-[#00C853] to-[#A6FF00] transition-all duration-500" 
              :style="{ width: occupancyPct + '%' }"
            ></div>
          </div>
          <div class="flex items-center justify-between mt-2.5 text-[9px] text-text-muted">
            <span>Lots filled: <strong class="text-text-primary font-bold">{{ totalVehicles }}</strong></span>
            <span>Capacity: <strong class="text-text-primary font-bold">60</strong></span>
          </div>
        </div>

        <!-- Sidebar footer -->
        <div class="pt-4 mt-4 border-t border-border/50">
          <div class="space-y-1">
            <!-- Exit Contractor View Mode button -->
            <button
              v-if="isContractorViewMode"
              @click="handleExitContractorView"
              class="w-full flex items-center px-3 py-2 text-sm font-bold text-success rounded-xl hover:bg-success/5 transition-colors duration-200"
            >
              <ArrowRightOnRectangleIcon class="mr-3 h-5 w-5 opacity-70" />
              Exit Contractor View
            </button>
            
            <!-- Exit Company View Mode button -->
            <button
              v-if="authStore.isCompanyViewMode"
              @click="handleExitCompanyView"
              class="w-full flex items-center px-3 py-2 text-sm font-bold text-primary rounded-xl hover:bg-[#A6FF00]/5 transition-colors duration-200"
            >
              <ArrowRightOnRectangleIcon class="mr-3 h-5 w-5 opacity-70" />
              Exit Company View
            </button>
            
            <!-- Settings link -->
            <router-link
              to="/app/settings"
              class="flex items-center px-3 py-2 text-sm font-bold text-text-secondary rounded-xl hover:bg-surface-hover hover:text-[#A6FF00] transition-colors duration-200"
              @click="$emit('navigate')"
            >
              <Cog6ToothIcon class="mr-3 h-5 w-5 text-text-muted" />
              Settings
            </router-link>
            
            <!-- Logout button -->
            <button
              @click="handleLogout"
              class="w-full flex items-center px-3 py-2 text-sm font-bold text-danger rounded-xl hover:bg-danger/5 transition-colors duration-200"
            >
              <ArrowRightOnRectangleIcon class="mr-3 h-5 w-5 opacity-70" />
              Logout
            </button>
          </div>
          
          <!-- Version info -->
          <div class="mt-4 px-3">
            <p class="text-[10px] text-text-muted text-center font-bold uppercase tracking-widest">
              v1.0.0
            </p>
          </div>
        </div>
      </nav>

    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '../../store/auth'
import { useDashboardStore } from '../../store/dashboard'
import { useToast } from 'vue-toastification'
import {
  HomeIcon,
  UsersIcon,
  BuildingOfficeIcon,
  MapPinIcon,
  TruckIcon,
  CreditCardIcon,
  DevicePhoneMobileIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  XMarkIcon,
  ShieldCheckIcon,
  IdentificationIcon,
  ClipboardDocumentListIcon,
  CalculatorIcon,
  BuildingStorefrontIcon,
  CubeIcon,
  ShoppingCartIcon,
  DocumentTextIcon,
  ReceiptPercentIcon,
  BeakerIcon,
  AdjustmentsHorizontalIcon,
  RectangleStackIcon
} from '@heroicons/vue/24/outline'

const iconMap = {
  HomeIcon,
  UsersIcon,
  BuildingOfficeIcon,
  MapPinIcon,
  TruckIcon,
  CreditCardIcon,
  DevicePhoneMobileIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  XMarkIcon,
  ShieldCheckIcon,
  IdentificationIcon,
  ClipboardDocumentListIcon,
  CalculatorIcon,
  BuildingStorefrontIcon,
  CubeIcon,
  ShoppingCartIcon,
  DocumentTextIcon,
  ReceiptPercentIcon,
  BeakerIcon,
  AdjustmentsHorizontalIcon,
  RectangleStackIcon
}

// Props
const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false
  }
})

// Emits
const emit = defineEmits(['close', 'navigate'])

// Composables
const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const dashboardStore = useDashboardStore()
const toast = useToast()

// Computed
const totalVehicles = computed(() => {
  const adminStats = dashboardStore.adminStats
  return (adminStats?.currentIn || []).reduce((s, i) => s + (i.count || 0), 0)
})

const occupancyPct = computed(() => {
  if (!totalVehicles.value) return 0
  return Math.min(Math.round((totalVehicles.value / 60) * 100), 100)
})

// Computed
const userInitials = computed(() => {
  if (!authStore.user) return 'U'
  const { firstName, lastName } = authStore.user
  return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase()
})

const isContractorViewMode = computed(() => {
  try {
    const stored = localStorage.getItem('contractorViewMode')
    return !!stored
  } catch {
    return false
  }
})

const navigationSections = computed(() => {
  const actualUserRole = authStore.userRole
  
  // Check if in contractor view mode
  const isContractorView = (() => {
    try {
      const stored = localStorage.getItem('contractorViewMode')
      return !!stored
    } catch {
      return false
    }
  })()
  
  // Determine active role for navigation mapping
  const activeRole = (isContractorView && actualUserRole === 'Contractor') ? 'Contractor' : authStore.userRole
  
  // Determine if company has contractors enabled
  const hasContractors = authStore.isCompanyViewMode
    ? (authStore.companyViewMode?.settings?.hasContractors === true || authStore.companyViewMode?.hasContractors === true)
    : (authStore.user?.company?.settings?.hasContractors === true || authStore.user?.company?.hasContractors === true)

  // Determine if company has reports enabled (defaults to true unless explicitly set to false)
  const hasReports = authStore.isCompanyViewMode
    ? (authStore.companyViewMode?.settings?.hasReports !== false && authStore.companyViewMode?.hasReports !== false)
    : (authStore.user?.company?.settings?.hasReports !== false && authStore.user?.company?.hasReports !== false)

  // Map and filter navigation sections dynamically
  const resolvedSections = authStore.sidebarNavigation.map(section => {
    const filteredItems = section.items.filter(item => {
      // Never show Reports tab for PremierAdmin
      if (item.path === '/app/reports' && activeRole === 'PremierAdmin') return false
      // Check role access
      if (!item.roles.includes(activeRole)) return false
      // Check hasContractors condition
      if (item.requiresHasContractors && !hasContractors) return false
      // Check hasReports condition
      if (item.requiresHasReports && !hasReports) return false
      return true
    }).map(item => ({
      ...item,
      icon: iconMap[item.icon] || HomeIcon
    }))
    
    return {
      ...section,
      items: filteredItems
    }
  }).filter(section => section.items.length > 0)

  // In Contractor view mode or Company view mode, flatten sections to a single "Main" section
  if ((isContractorView && actualUserRole === 'Contractor') || 
      (authStore.isCompanyViewMode && authStore.companyViewMode && actualUserRole === 'Administrator')) {
    const flattenedItems = []
    resolvedSections.forEach(sec => {
      flattenedItems.push(...sec.items)
    })
    return [
      {
        name: 'Main',
        items: flattenedItems
      }
    ]
  }

  return resolvedSections
})

// Methods
const isActive = (path) => {
  return route.path === path || route.path.startsWith(path + '/')
}

const handleExitContractorView = () => {
  try {
    const stored = localStorage.getItem('contractorViewMode')
    if (stored) {
      const contractorViewData = JSON.parse(stored)
      
      // Restore original user data
      if (contractorViewData.originalUser) {
        authStore.user = contractorViewData.originalUser
        localStorage.setItem('user', JSON.stringify(contractorViewData.originalUser))
      }
      
      if (contractorViewData.originalToken) {
        authStore.token = contractorViewData.originalToken
        localStorage.setItem('token', contractorViewData.originalToken)
      }
    }
    
    // Remove contractor view mode
    localStorage.removeItem('contractorViewMode')
    
    router.push('/app/contractors')
    toast.success('Exited contractor view mode')
  } catch (error) {
    console.error('Error exiting contractor view:', error)
    toast.error('Failed to exit contractor view')
  }
}

const handleExitCompanyView = () => {
  authStore.clearCompanyViewMode()
  router.push('/app/companies')
  toast.success('Exited company view mode')
}

const handleLogout = async () => {
  try {
    // Clear contractor view mode if exists
    localStorage.removeItem('contractorViewMode')
    
    await authStore.logout()
    router.push('/login')
    toast.success('Logged out successfully')
  } catch (error) {
    toast.error('Logout failed')
  }
}
</script>

<style scoped>
/* Custom scrollbar for sidebar */
.overflow-y-auto::-webkit-scrollbar {
  width: 4px;
}
.overflow-y-auto::-webkit-scrollbar-track {
  background: transparent;
}
.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 2px;
}
.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Nav item base */
.nav-item {
  border-left: 3px solid transparent;
  position: relative;
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
}

/* Active pill state */
.nav-item-active {
  background: linear-gradient(to right, rgba(166, 255, 0, 0.08), rgba(166, 255, 0, 0.01));
  border-left-color: #A6FF00;
  box-shadow: 0 0 15px rgba(166, 255, 0, 0.12);
}

.dark .nav-item-active {
  background: linear-gradient(to right, rgba(166, 255, 0, 0.08), rgba(166, 255, 0, 0.01));
  border-left-color: #A6FF00;
  box-shadow: 0 0 15px rgba(166, 255, 0, 0.12);
}

/* Hover state */
.nav-item-hover:hover {
  background: rgba(166, 255, 0, 0.05);
  border-left-color: rgba(166, 255, 0, 0.3);
  box-shadow: 0 0 8px rgba(166, 255, 0, 0.04);
}

.dark .nav-item-hover:hover {
  background: rgba(166, 255, 0, 0.05);
  border-left-color: rgba(166, 255, 0, 0.3);
  box-shadow: 0 0 8px rgba(166, 255, 0, 0.04);
}
</style>
